import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, X, Loader2, Mail, FileText, Bot, Sparkles, AlertCircle } from 'lucide-react';
import axios from 'axios';

const SupportChatWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [mode, setMode] = useState('chat'); // 'chat' | 'ticket'
    const [messages, setMessages] = useState([
        { role: 'model', text: '¡Hola! Soy el asistente inteligente de **Archi 24/7**. ¿En qué puedo ayudarte hoy?\n\nSi experimentas un problema técnico, puedes presionar **"Crear Ticket de Soporte"** para reportarlo por correo electrónico.' }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);

    // Formulario de Ticket
    const [ticketName, setTicketName] = useState('');
    const [ticketEmail, setTicketEmail] = useState('');
    const [ticketSubject, setTicketSubject] = useState('');
    const [ticketDesc, setTicketDesc] = useState('');
    const [ticketLoading, setTicketLoading] = useState(false);
    const [ticketStatus, setTicketStatus] = useState(null); // { success: boolean, message: string }

    const chatEndRef = useRef(null);

    // Auto-scroll al final del chat
    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, loading]);

    // Intentar rellenar datos del usuario desde localStorage
    useEffect(() => {
        if (isOpen && mode === 'ticket') {
            const token = localStorage.getItem('token');
            if (token) {
                try {
                    // Decodificar JWT simple (segunda parte de la cadena base64)
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    if (payload) {
                        setTicketName(payload.name || payload.username || '');
                        setTicketEmail(payload.email || '');
                    }
                } catch (e) {
                    console.error("Error decodificando token en soporte:", e);
                }
            }
        }
    }, [isOpen, mode]);

    const handleSendMessage = async (textToSend) => {
        const msgText = textToSend || input;
        if (!msgText.trim()) return;

        if (!textToSend) setInput('');
        
        // Agregar mensaje del usuario a la UI
        const newMessages = [...messages, { role: 'user', text: msgText }];
        setMessages(newMessages);
        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            // Mapear historial en el formato que espera el backend { role, text }
            const history = messages.map(m => ({ role: m.role, text: m.text }));

            const response = await axios.post('http://localhost:4000/api/support/chat', {
                message: msgText,
                history
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setMessages(prev => [...prev, { role: 'model', text: response.data.response }]);
        } catch (err) {
            console.error("Error en chat de soporte:", err);
            setMessages(prev => [...prev, { role: 'model', text: '⚠️ Hubo un error de comunicación con el asistente. Por favor, inténtalo más tarde o abre un ticket de soporte.' }]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTicket = async (e) => {
        e.preventDefault();
        if (!ticketName || !ticketEmail || !ticketSubject || !ticketDesc) return;

        setTicketLoading(true);
        setTicketStatus(null);

        try {
            const token = localStorage.getItem('token');
            await axios.post('http://localhost:4000/api/support/ticket', {
                name: ticketName,
                email: ticketEmail,
                subject: ticketSubject,
                description: ticketDesc
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setTicketStatus({ success: true, message: '¡Ticket enviado con éxito! Nuestro equipo se pondrá en contacto contigo pronto.' });
            // Limpiar campos
            setTicketSubject('');
            setTicketDesc('');
            // Regresar al chat tras 3 segundos
            setTimeout(() => {
                setMode('chat');
                setTicketStatus(null);
            }, 4000);
        } catch (err) {
            console.error("Error enviando ticket:", err);
            setTicketStatus({ success: false, message: err.response?.data?.error || 'No se pudo enviar el ticket. Revisa tu conexión.' });
        } finally {
            setTicketLoading(false);
        }
    };

    // Función para renderizar markdown básico (negritas, saltos de línea)
    const renderMessageText = (text) => {
        if (!text) return '';
        // Reemplazar saltos de línea
        const parts = text.split('\n');
        return parts.map((part, i) => {
            // Reemplazar **texto** por <strong>texto</strong>
            const regex = /\*\*(.*?)\*\*/g;
            const subParts = [];
            let lastIndex = 0;
            let match;

            while ((match = regex.exec(part)) !== null) {
                // Texto antes de la negrita
                if (match.index > lastIndex) {
                    subParts.push(part.substring(lastIndex, match.index));
                }
                // Texto en negrita
                subParts.push(<strong key={match.index} className="font-bold text-purple-300">{match[1]}</strong>);
                lastIndex = regex.lastIndex;
            }

            if (lastIndex < part.length) {
                subParts.push(part.substring(lastIndex));
            }

            return (
                <p key={i} className="mb-2 last:mb-0 leading-relaxed text-xs">
                    {subParts.length > 0 ? subParts : part}
                </p>
            );
        });
    };

    return (
        <div className="fixed bottom-6 right-6 z-50 font-sans">
            {/* BURBUJA DE CHAT FLOTANTE */}
            {!isOpen && (
                <button
                    onClick={() => setIsOpen(true)}
                    className="w-14 h-14 bg-gradient-to-tr from-purple-600 to-indigo-500 hover:from-purple-500 hover:to-indigo-400 text-white rounded-full flex items-center justify-center shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer border border-purple-500/20 group relative"
                >
                    <MessageSquare size={24} className="group-hover:rotate-6 transition-transform" />
                    <span className="absolute right-0 top-0 w-3 h-3 bg-emerald-400 rounded-full border-2 border-theme-bg animate-pulse"></span>
                </button>
            )}

            {/* PANEL DE CHAT */}
            {isOpen && (
                <div className="bg-theme-panel/95 backdrop-blur-xl border border-theme-border w-96 rounded-3xl shadow-2xl flex flex-col max-h-[calc(100vh-100px)] h-[550px] overflow-hidden animate-fade-in">
                    
                    {/* CABECERA */}
                    <div className="bg-gradient-to-r from-purple-900/60 to-indigo-900/60 border-b border-theme-border/50 p-4 flex justify-between items-center shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-500/10 rounded-2xl border border-purple-500/30 flex items-center justify-center text-purple-400">
                                {mode === 'chat' ? <Bot size={20} className="animate-pulse" /> : <Mail size={20} />}
                            </div>
                            <div>
                                <h4 className="text-sm font-black text-theme-text-primary flex items-center gap-1.5 leading-tight">
                                    {mode === 'chat' ? 'Asistente Archi' : 'Crear Ticket'} <Sparkles size={12} className="text-purple-400" />
                                </h4>
                                <span className="text-[10px] text-emerald-400 flex items-center gap-1 mt-0.5">
                                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span> Soporte Técnico Activo
                                </span>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            {mode === 'chat' ? (
                                <button
                                    onClick={() => setMode('ticket')}
                                    className="px-2.5 py-1.5 bg-theme-bg border border-theme-border hover:bg-theme-border/20 rounded-xl text-[10px] font-bold text-theme-text-primary transition-all cursor-pointer flex items-center gap-1"
                                >
                                    <FileText size={10} /> Crear Ticket
                                </button>
                            ) : (
                                <button
                                    onClick={() => setMode('chat')}
                                    className="px-2.5 py-1.5 bg-theme-bg border border-theme-border hover:bg-theme-border/20 rounded-xl text-[10px] font-bold text-theme-text-primary transition-all cursor-pointer"
                                >
                                    Volver al Chat
                                </button>
                            )}
                            <button
                                onClick={() => setIsOpen(false)}
                                className="p-1.5 bg-theme-bg border border-theme-border hover:bg-theme-border/20 rounded-xl text-slate-500 hover:text-theme-text-primary transition-all cursor-pointer"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    </div>

                    {/* CUERPO DEL CHAT (MODO CONVERSACIÓN) */}
                    {mode === 'chat' && (
                        <>
                            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                {messages.map((msg, i) => (
                                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[85%] p-3.5 rounded-2xl text-xs leading-relaxed border ${
                                            msg.role === 'user'
                                                ? 'bg-purple-600/15 border-purple-500/20 text-theme-text-primary rounded-br-none'
                                                : 'bg-theme-bg border-theme-border text-theme-text-primary rounded-bl-none'
                                        }`}>
                                            {renderMessageText(msg.text)}
                                        </div>
                                    </div>
                                ))}

                                {loading && (
                                    <div className="flex justify-start">
                                        <div className="bg-theme-bg border border-theme-border p-3.5 rounded-2xl rounded-bl-none flex items-center gap-2 text-xs text-theme-text-secondary">
                                            <Loader2 size={14} className="animate-spin text-purple-400" />
                                            <span>Asistente está escribiendo...</span>
                                        </div>
                                    </div>
                                )}
                                <div ref={chatEndRef} />
                            </div>

                            {/* RECOMENDACIONES / RESPUESTAS RÁPIDAS */}
                            <div className="px-4 py-2 border-t border-theme-border/30 flex gap-2 overflow-x-auto shrink-0 custom-scrollbar select-none">
                                <button
                                    onClick={() => handleSendMessage('¿Cómo configuro una cámara en vivo?')}
                                    className="px-3 py-1.5 bg-theme-bg border border-theme-border hover:border-purple-500/30 rounded-xl text-[10px] text-theme-text-secondary hover:text-theme-text-primary transition-all shrink-0 cursor-pointer"
                                >
                                    Configurar cámara
                                </button>
                                <button
                                    onClick={() => handleSendMessage('¿Cuáles son los planes de suscripción?')}
                                    className="px-3 py-1.5 bg-theme-bg border border-theme-border hover:border-purple-500/30 rounded-xl text-[10px] text-theme-text-secondary hover:text-theme-text-primary transition-all shrink-0 cursor-pointer"
                                >
                                    Planes y Precios
                                </button>
                                <button
                                    onClick={() => handleSendMessage('¿Cómo se usa el cronograma Gantt?')}
                                    className="px-3 py-1.5 bg-theme-bg border border-theme-border hover:border-purple-500/30 rounded-xl text-[10px] text-theme-text-secondary hover:text-theme-text-primary transition-all shrink-0 cursor-pointer"
                                >
                                    Cronograma Gantt
                                </button>
                            </div>

                            {/* INPUT DE ENVÍO */}
                            <form
                                onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}
                                className="p-4 border-t border-theme-border/50 flex gap-2 shrink-0 bg-theme-bg/30"
                            >
                                <input
                                    type="text"
                                    placeholder="Escribe tu consulta aquí..."
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    disabled={loading}
                                    className="flex-1 bg-theme-bg border border-theme-border rounded-xl px-4 py-2.5 text-xs text-theme-text-primary focus:outline-none focus:border-purple-500/50 disabled:opacity-50"
                                />
                                <button
                                    type="submit"
                                    disabled={loading || !input.trim()}
                                    className="bg-purple-600 hover:bg-purple-500 text-white p-2.5 rounded-xl transition-all cursor-pointer disabled:opacity-40 shrink-0"
                                >
                                    <Send size={14} />
                                </button>
                            </form>
                        </>
                    )}

                    {/* CUERPO DEL TICKET (MODO FORMULARIO) */}
                    {mode === 'ticket' && (
                        <form onSubmit={handleCreateTicket} className="flex-1 flex flex-col justify-between p-4 overflow-y-auto space-y-4">
                            <div className="space-y-3">
                                <h5 className="text-xs font-bold text-theme-text-primary uppercase tracking-wider mb-2">Reportar Incidencia por Email</h5>
                                
                                {ticketStatus && (
                                    <div className={`p-3 rounded-2xl text-xs flex items-start gap-2 border ${
                                        ticketStatus.success 
                                            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-450' 
                                            : 'bg-rose-500/10 border-rose-500/30 text-rose-450'
                                    }`}>
                                        <AlertCircle size={16} className="shrink-0 mt-0.5" />
                                        <span>{ticketStatus.message}</span>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[10px] font-bold uppercase text-theme-text-secondary mb-1">Nombre</label>
                                        <input
                                            type="text"
                                            required
                                            value={ticketName}
                                            onChange={(e) => setTicketName(e.target.value)}
                                            className="w-full bg-theme-bg border border-theme-border rounded-xl p-2 text-xs text-theme-text-primary focus:outline-none focus:border-purple-500/50"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold uppercase text-theme-text-secondary mb-1">Tu Correo</label>
                                        <input
                                            type="email"
                                            required
                                            value={ticketEmail}
                                            onChange={(e) => setTicketEmail(e.target.value)}
                                            className="w-full bg-theme-bg border border-theme-border rounded-xl p-2 text-xs text-theme-text-primary focus:outline-none focus:border-purple-500/50 font-mono"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-theme-text-secondary mb-1">Asunto / Problema</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="Ej: La cámara 2 no transmite en vivo"
                                        value={ticketSubject}
                                        onChange={(e) => setTicketSubject(e.target.value)}
                                        className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-xs text-theme-text-primary focus:outline-none focus:border-purple-500/50"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-bold uppercase text-theme-text-secondary mb-1">Descripción del Problema</label>
                                    <textarea
                                        required
                                        rows={4}
                                        placeholder="Describe paso a paso lo que sucede..."
                                        value={ticketDesc}
                                        onChange={(e) => setTicketDesc(e.target.value)}
                                        className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-xs text-theme-text-primary focus:outline-none focus:border-purple-500/50 resize-none font-sans"
                                    />
                                </div>
                            </div>

                            <div className="pt-2 border-t border-theme-border/30 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setMode('chat')}
                                    className="flex-1 bg-theme-bg hover:bg-theme-border/20 border border-theme-border py-2.5 text-theme-text-secondary hover:text-theme-text-primary rounded-xl font-bold text-xs transition-all cursor-pointer"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={ticketLoading}
                                    className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-500 py-2.5 text-white rounded-xl font-bold text-xs shadow-lg hover:scale-[1.01] transition-all cursor-pointer disabled:opacity-50"
                                >
                                    {ticketLoading ? (
                                        <div className="flex items-center justify-center gap-1.5">
                                            <Loader2 size={12} className="animate-spin" />
                                            Enviando...
                                        </div>
                                    ) : 'Enviar Ticket'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            )}
        </div>
    );
};

export default SupportChatWidget;
